import { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import { useGlobalStore } from '../../stores/globalStore';
import { locate } from '../../navigation/navConfig';
import './MobileTabBar.css';

/**
 * Phone navigation.
 *
 * On a phone the sidebar is a drawer behind a hamburger, which means every
 * move between screens costs two taps and a decision about where the menu is.
 * The pattern people already know from every phone app is a bar of tabs along
 * the bottom, and the bottom of the screen is where a thumb rests.
 *
 * Four tabs plus More, because four is what fits a 390px screen with legible
 * labels — five 78px slots, of which the fifth opens the rest. It replaces the
 * status strip on a phone, either way up, rather than sitting beside it: two
 * stacked bars would eat a quarter of the viewport, and the status strip's contents
 * (connection, version, support chat) are reference material, not something
 * you reach for mid-shift. They move into the More sheet.
 */

export interface TabItem {
  label: string;
  icon: string;
  route: string;
  /** Where tapping the tab goes, when not `route` itself. */
  to?: string;
  /** Also highlight this tab on these route prefixes. */
  alsoMatches?: string[];
  /** The navigation section (navConfig) the tab stands for: it is lit on every page of that section. */
  section?: string;
}

/**
 * The four are the screens a phone user is actually on: a resident's record,
 * today's menu, taking menu choices, and where they started. Everything
 * administrative — Purchasing, EMR, Reports, Tables — is desk work and lives
 * behind More.
 */
export const PHONE_TABS: TabItem[] = [
  { label: 'Home', icon: 'bi-house', route: '/dashboard', section: 'home' },
  { label: 'Residents', icon: 'bi-people', route: '/people', alsoMatches: ['/people/'], section: 'residents' },
  // Today's menu: menu editing is closed on a phone (DesktopOrTabletOnly). The menu cycle
  // list is a button on that page, and the tab stays lit anywhere under /menucycle.
  { label: 'Menus', icon: 'bi-calendar3', route: '/menucycle', to: '/menucycle/today', alsoMatches: ['/menucycle/'], section: 'menus' },
  // Selective Touch: taking residents' menu choices at the bedside is the phone's main job in
  // the kitchen's day. The Kitchen display is a wall screen; it is under Kitchen in More.
  { label: 'Touch', icon: 'bi-hand-index-thumb', route: '/selective-touch' },
];

/**
 * Which tab a path belongs to.
 *
 * Longest match wins, so `/people/27237` selects Residents rather than
 * whichever tab happens to be listed first with a `/` prefix. Exported for the
 * tests, which is the only way to check this without mounting a router.
 */
export function activeTabFor(pathname: string, tabs: TabItem[] = PHONE_TABS): TabItem | null {
  let best: TabItem | null = null;
  let bestLen = -1;
  for (const tab of tabs) {
    for (const prefix of [tab.route, ...(tab.alsoMatches ?? [])]) {
      const hit = pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : prefix + '/');
      if (hit && prefix.length > bestLen) {
        best = tab;
        bestLen = prefix.length;
      }
    }
  }
  return best;
}

/**
 * Which tab, or More, stands for the page at `pathname`.
 *
 * A tab's own routes come first (Selective Touch is a Kitchen page, but it has the Touch tab).
 * Otherwise the page's section in the navigation (navConfig's `locate`, the lookup the drawer uses
 * for its active section) decides: Census and Weight Entry are Residents pages, Global Meal Patterns
 * is a Menus page. A page whose section has no tab lives behind More, so More is lit. A page that
 * no section owns (Help) lights nothing.
 */
export function currentTabFor(
  pathname: string,
  facilityId: number | null | undefined,
  tabs: TabItem[] = PHONE_TABS,
): { tab: TabItem | null; more: boolean } {
  const own = activeTabFor(pathname, tabs);
  if (own) return { tab: own, more: false };
  const sectionId = locate(pathname, facilityId)?.section.id;
  if (!sectionId) return { tab: null, more: false };
  const tab = tabs.find((t) => t.section === sectionId);
  return tab ? { tab, more: false } : { tab: null, more: true };
}

export default function MobileTabBar({
  onMore,
  moreOpen,
}: {
  onMore: () => void;
  moreOpen: boolean;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const residentPluralLabel = useGlobalStore((s) => s.residentPluralLabel);
  const facilityId = useGlobalStore((s) => s.facilityId);

  const tabs = useMemo(
    // Facilities rename Residents; the tab has to follow or it names something
    // the site does not have.
    () => PHONE_TABS.map((t) => (t.route === '/people' ? { ...t, label: residentPluralLabel } : t)),
    [residentPluralLabel],
  );

  const current = currentTabFor(pathname, facilityId, tabs);
  const active = moreOpen ? null : current.tab;
  const moreLit = moreOpen || current.more;

  return (
    <nav className="mobile-tab-bar" aria-label="Primary">
      {tabs.map((tab) => {
        const isActive = active?.route === tab.route;
        return (
          <button
            key={tab.route}
            type="button"
            className={isActive ? 'mtb-tab is-active' : 'mtb-tab'}
            onClick={() => navigate(tab.to ?? tab.route)}
            aria-current={isActive ? 'page' : undefined}
          >
            <i className={`bi ${tab.icon}`} aria-hidden="true" />
            <span>{tab.label}</span>
          </button>
        );
      })}
      <button
        type="button"
        className={moreLit ? 'mtb-tab is-active' : 'mtb-tab'}
        onClick={onMore}
        aria-expanded={moreOpen}
        aria-haspopup="dialog"
      >
        <i className="bi bi-three-dots" aria-hidden="true" />
        <span>More</span>
      </button>
    </nav>
  );
}

/**
 * The sheet behind More.
 *
 * A bottom sheet rather than the sidebar drawer: it opens from the control
 * that summoned it, which keeps the thumb where it already is.
 *
 * Other phone sheets reuse it. Given a `label`, the sheet is announced as a
 * dialog with that name; the Drawer already traps focus and closes on Escape
 * and on a tap outside.
 */
export function MobileMoreSheet({
  open,
  onClose,
  label,
  children,
}: {
  open: boolean;
  onClose: () => void;
  label?: string;
  children: React.ReactNode;
}) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      // The Drawer's root (backdrop and sheet), so print can hide the whole thing.
      className="mtb-sheet-root"
      PaperProps={{
        className: 'mtb-sheet',
        sx: { maxHeight: '80vh', borderTopLeftRadius: 12, borderTopRightRadius: 12 },
        ...(label ? { role: 'dialog', 'aria-modal': true, 'aria-label': label } : {}),
      }}
    >
      <div className="mtb-sheet-grabber" aria-hidden="true" />
      {children}
    </Drawer>
  );
}

/** Convenience state hook so MainLayout does not grow another two useStates. */
export function useMoreSheet() {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
}
